import { Component } from '@theme/component';
import { getStore } from '@theme/it-pdp-store';

/**
 * IT PDP — size & fit grids.
 *
 * Pushes each choice into the store and re-marks which values are still in
 * stock given the other choices. Also answers the buy block's nudge: when a
 * customer presses Add to bag with an option still unchosen, the buy block
 * fires `it:nudge-option` and the matching grid shakes and takes focus.
 */
class ItPdpSizePicker extends Component {
  #unsubscribe;

  connectedCallback() {
    super.connectedCallback();

    this.#store = getStore(this);
    this.addEventListener('change', this.#onChange);

    const root = this.closest('[data-it-pdp]') ?? document;
    root.addEventListener('it:nudge-option', this.#onNudge);
    this.#root = root;

    if (!this.#store) return;

    // A single-value option is checked in the markup because there is nothing
    // to choose; push that into the store so it counts as answered.
    for (const input of this.querySelectorAll('input:checked')) {
      this.#store.selectOption(input.dataset.optionName, input.value);
    }

    this.#unsubscribe = this.#store.subscribe((state) => {
      this.#syncChecked(state.selections);
      this.#markAvailability();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unsubscribe?.();
    this.#root?.removeEventListener('it:nudge-option', this.#onNudge);
  }

  #store = null;
  #root = null;

  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    this.#store?.selectOption(input.dataset.optionName, input.value);
  };

  /**
   * The store is the source of truth for what is chosen — a deep link with
   * ?variant= seeds it before the markup ever renders a checked input.
   */
  #syncChecked(selections) {
    for (const input of this.querySelectorAll('input[data-option-name]')) {
      const chosen = selections[input.dataset.optionName];
      input.checked = chosen === input.value;
    }
  }

  #markAvailability() {
    for (const grid of this.querySelectorAll('[data-option-name]')) {
      const name = grid.dataset.optionName;
      const available = this.#store.availableValues(name);
      const existing = this.#store.existingValues(name);

      for (const pill of grid.querySelectorAll('.it-sizes__pill')) {
        const input = pill.querySelector('input');
        const exists = existing.has(input.value);
        const inStock = available.has(input.value);

        // A size this colour was never cut in has nothing to buy or to notify
        // on, so it leaves the grid. A size that exists but is sold out stays,
        // greyed: picking it is how the shopper reaches Notify me.
        pill.classList.toggle('is-absent', !exists);
        pill.classList.toggle('is-out', exists && !inStock);
        pill.title = exists && !inStock ? 'Out of stock' : '';
      }
    }
  }

  #onNudge = (event) => {
    const grid = this.querySelector(`[data-option-name="${CSS.escape(event.detail.optionName)}"]`);
    if (!grid) return;

    grid.scrollIntoView({ block: 'center', behavior: 'smooth' });

    // Restart the animation: removing the class alone does not rewind it.
    grid.classList.remove('is-asking');
    void grid.offsetWidth;
    grid.classList.add('is-asking');

    grid.querySelector('input')?.focus({ preventScroll: true });
  };
}

if (!customElements.get('it-pdp-size-picker')) {
  customElements.define('it-pdp-size-picker', ItPdpSizePicker);
}
